import { TFilterField, TFilterValue } from "@irohana-dev/components";
import { TOption } from "@/types/common";
import { applyColumnSettings as applyKitColumnSettings, asStringArray, buildColumnOptions, fromDateRangeValue } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import {
  JOB_MULTI_SELECT_FILTER_KEYS,
  TJobDisplayColumnSetting,
  TJobFilterFormExtended,
  defaultJobDisplayColumns,
  defaultJobFilterValues,
} from "@/features/job/components/job-list/job-filter-shared";

const DATE_RANGE_KEY = "registrationDate";

type TJobFilterOptions = {
  status: TOption[];
  tagIds: TOption[];
  managementNumber: TOption[];
  companyName: TOption[];
  jobName: TOption[];
  recruiter: TOption[];
  industry: TOption[];
  japaneseLevelType: TOption[];
  workplace: TOption[];
  recruitmentLocationType: TOption[];
};

export const buildJobFilterFields = (options: TJobFilterOptions): TFilterField[] => [
  { type: "multiSelect", key: "status", label: "ステータス", options: options.status, placeholder: "選択してください" },
  { type: "multiSelect", key: "tagIds", label: "タグ", options: options.tagIds, placeholder: "選択してください" },
  { type: "freeText", key: "managementNumber", label: "求人管理番号", options: options.managementNumber, placeholder: "入力してください" },
  { type: "multiSelect", key: "companyName", label: "企業名", options: options.companyName, placeholder: "選択してください" },
  { type: "freeText", key: "jobName", label: "求人タイトル", options: options.jobName, placeholder: "入力してください" },
  { type: "multiSelect", key: "recruiterId", label: "求人担当者", options: options.recruiter, placeholder: "選択してください" },
  { type: "multiSelect", key: "companySpecificIndustryId", label: "分野", options: options.industry, placeholder: "選択してください" },
  {
    type: "multiSelect",
    key: "japaneseLevelType",
    label: "必要な日本語レベル",
    options: options.japaneseLevelType,
    placeholder: "選択してください",
  },
  { type: "text", key: "recruitmentNumber", label: "募集人数", placeholder: "入力してください", suffix: "人", inputMode: "numeric" },
  { type: "freeText", key: "workplace", label: "勤務場所", options: options.workplace, placeholder: "入力してください" },
  {
    type: "multiSelect",
    key: "recruitmentLocationType",
    label: "募集地",
    options: options.recruitmentLocationType,
    placeholder: "選択してください",
  },
  { type: "dateRange", key: DATE_RANGE_KEY, label: "登録日" },
];

export const buildJobColumnOptions = buildColumnOptions;

export const toFilterValue = (filters: TJobFilterFormExtended): TFilterValue => {
  const value: TFilterValue = {};
  for (const [key, raw] of Object.entries(filters)) {
    if (key === "registrationDateFrom" || key === "registrationDateTo") continue;
    if (Array.isArray(raw)) {
      if (raw.length > 0) value[key] = raw.map(String);
      continue;
    }
    if (raw === "" || raw === null || raw === undefined) continue;
    if (JOB_MULTI_SELECT_FILTER_KEYS.includes(key as keyof TJobFilterFormExtended)) {
      value[key] = [String(raw)];
      continue;
    }
    value[key] = String(raw);
  }
  const { registrationDateFrom, registrationDateTo } = filters;
  if (registrationDateFrom || registrationDateTo) {
    value[DATE_RANGE_KEY] = { from: registrationDateFrom, to: registrationDateTo };
  }
  return value;
};

export const fromFilterValue = (value: TFilterValue): TJobFilterFormExtended => {
  const range = fromDateRangeValue(value, DATE_RANGE_KEY);
  return {
    ...defaultJobFilterValues,
    status: asStringArray(value.status),
    tagIds: asStringArray(value.tagIds),
    managementNumber: (value.managementNumber as string) ?? "",
    companyName: asStringArray(value.companyName),
    jobName: (value.jobName as string) ?? "",
    recruiterId: asStringArray(value.recruiterId),
    companySpecificIndustryId: asStringArray(value.companySpecificIndustryId),
    japaneseLevelType: asStringArray(value.japaneseLevelType),
    recruitmentNumber: (value.recruitmentNumber as string) ?? "",
    workplace: (value.workplace as string) ?? "",
    recruitmentLocationType: asStringArray(value.recruitmentLocationType),
    registrationDateFrom: range.from,
    registrationDateTo: range.to,
  };
};

export const applyColumnSettings = (columns: TJobDisplayColumnSetting[], order: string[], hidden: string[]): TJobDisplayColumnSetting[] =>
  applyKitColumnSettings(columns, order, hidden, defaultJobDisplayColumns);
